<template>
  <div class="limit-reached">
    <!-- The sample-complete landing (spec 2026-08-03, render E), mirroring
         FreeChatView: shown for every free-sample account, with the legacy block
         below unchanged for every monthly allowance. -->
    <template v-if="showSampleComplete">
      <div class="sample-icon">
        <font-awesome-icon icon="check-circle" class="icon" aria-hidden="true" />
      </div>
      <h2>{{ $t('course.sampleComplete.title') }}</h2>
      <ul class="sample-recap" role="list">
        <li class="recap-row">
          <font-awesome-icon icon="check" class="recap-icon recap-icon-done" aria-hidden="true" />
          <span>{{ $t('course.sampleComplete.lessonDone') }}</span>
        </li>
        <li v-if="hasFreeChatLeft" class="recap-row recap-row-waiting">
          <font-awesome-icon icon="gift" class="recap-icon recap-icon-waiting" aria-hidden="true" />
          <span>{{ $t('course.sampleComplete.chatsWaiting') }}</span>
        </li>
        <li v-else class="recap-row">
          <font-awesome-icon icon="check" class="recap-icon recap-icon-done" aria-hidden="true" />
          <span>{{ $t('course.sampleComplete.chatsDone') }}</span>
        </li>
      </ul>
      <p>{{ $t('course.sampleComplete.body') }}</p>
      <!-- Offered ABOVE the plans on purpose: what is still free comes
           before what costs money. -->
      <button
        v-if="hasFreeChatLeft"
        type="button"
        class="button sample-complete-cta"
        @click="$emit('go-to-chat')"
      >
        <font-awesome-icon icon="comments" class="button-icon" aria-hidden="true" />
        <span>{{ $t('course.sampleComplete.startChat') }}</span>
      </button>
    </template>
    <template v-else>
      <div class="limit-icon">
        <font-awesome-icon icon="hourglass-end" class="icon" />
      </div>
      <h2>{{ $t('course.limits.title') }}</h2>
      <p v-if="isFreePlan">
        {{ $t('course.limits.freePlan', { count: lessonLimit }) }}
      </p>
      <p v-else-if="currentPlanId === 'foundation'">
        {{ $t('course.limits.foundationPlan') }}
      </p>
      <p v-else>
        {{ $t('course.limits.fluencyPlan') }}
      </p>
    </template>

    <UpgradeOptions
      :current-plan-id="currentPlanId"
      :user-country="userCountry"
      :current-provider="currentProvider"
      :on-upgrade="onUpgrade"
      @iap-purchase-complete="$emit('iap-purchase-complete', $event)"
    />
  </div>
</template>

<script>
/**
 * The lesson allowance block: the sample-complete landing or the legacy
 * "limit reached" copy, with the plans below it.
 *
 * Extracted from LessonsView so ONE definition can serve two presentations —
 * the standing wall that replaces the course tree when nothing is resumable,
 * and the non-destructive upsell sheet a gated create tap raises over a tree
 * that must stay mounted (a learner who mis-taps lesson 2 has to be able to
 * dismiss and go back to resuming lesson 1). Duplicating the markup for the
 * second surface is how the two would start to disagree.
 *
 * Deliberately carries no copy of its own: every string is an existing i18n
 * key, so the 31-locale parity manifest and the literal-copy gate stay
 * untriggered.
 */
import UpgradeOptions from "@/components/UpgradeOptions.vue";

export default {
  name: "LessonLimitReached",
  components: { UpgradeOptions },
  props: {
    // Whether the softer sample-complete landing replaces the legacy copy.
    showSampleComplete: { type: Boolean, default: false },
    // Whether the account's free chat is still unspent, for the recap row and
    // the "go to chat" next step.
    hasFreeChatLeft: { type: Boolean, default: false },
    isFreePlan: { type: Boolean, default: false },
    currentPlanId: { type: String, default: "free" },
    // The freebies-adjusted lifetime ceiling the gate itself used, interpolated
    // into the free-plan copy so the number on screen is the number enforced.
    lessonLimit: { type: Number, default: 0 },
    userCountry: { type: String, default: "US" },
    currentProvider: { type: String, default: null },
    // Async `(planType) => Promise<void>`; UpgradeOptions owns the button state
    // until it settles.
    onUpgrade: { type: Function, required: true },
  },
  emits: ["go-to-chat", "iap-purchase-complete"],
};
</script>

<style scoped>
/* Limit Reached */
.limit-reached {
  text-align: center;
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: 3rem 2rem;
  background: var(--al-surface-raised);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-md);
  box-sizing: border-box;
}

/* Sheet presentation: the Modal host already supplies the surface, the radius
   and the elevation, so the block drops its own card chrome rather than
   stacking a second one inside the first. */
.limit-reached.limit-reached-sheet {
  padding: 0;
  background: transparent;
  box-shadow: none;
  border-radius: 0;
}

.limit-icon {
  font-size: 4rem;
  color: var(--al-orange);
  margin-bottom: 1.5rem;
}

.limit-reached h2 {
  font-size: 2.5rem;
  font-weight: 700;
  /* --al-primary at this size (>=24px) is ~3.3:1, meeting the AA large-text
     floor (3:1) against the white card background. */
  color: var(--al-primary);
  margin-bottom: 1.5rem;
}

.limit-reached p {
  font-size: 1.25rem;
  color: var(--al-text-muted);
  margin-bottom: 2.5rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Sample-complete landing, mirroring FreeChatView. DESIGN.md reserves the warm
   accent for celebration and upgrade emphasis, so the orb is the one warm
   element; the free next step stays primary blue and the paid CTAs belong to
   UpgradeOptions below. */
.sample-icon {
  width: 88px;
  height: 88px;
  margin: 0 auto 1.5rem;
  border-radius: var(--al-radius-pill);
  background: var(--al-warm-soft);
  color: var(--al-orange);
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sample-recap {
  list-style: none;
  margin: 0 auto 2rem;
  padding: 0;
  display: inline-flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
}

.recap-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.125rem;
  color: var(--al-text);
}

.recap-row-waiting {
  color: var(--al-text-muted);
}

.recap-icon {
  width: 1.25rem;
  flex: none;
}

.recap-icon-done {
  color: var(--al-success-dark);
}

.recap-icon-waiting {
  color: var(--al-orange);
}

.button.sample-complete-cta {
  padding: 0.75rem 1.5rem;
  margin-bottom: 2.5rem;
  font-size: 1rem;
  font-weight: 600;
  min-height: 48px;
  border: 1px solid var(--al-primary);
  border-radius: var(--al-radius-md);
  background: transparent;
  color: var(--al-primary-hover);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.button.sample-complete-cta:hover {
  background: var(--al-primary-soft);
}

.button.sample-complete-cta:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

@media (min-width: 768px) {
  .limit-reached {
    max-width: 800px;
    padding: 2rem;
  }

  .limit-reached.limit-reached-sheet {
    padding: 0;
  }

  .limit-reached h2 {
    font-size: 2rem;
  }

  .limit-reached p {
    font-size: 1.1rem;
  }
}

@media (max-width: 767px) {
  .limit-reached {
    padding: 1.5rem 1rem;
  }

  .limit-reached.limit-reached-sheet {
    padding: 0;
  }

  .limit-reached h2 {
    font-size: 1.75rem;
  }

  .limit-reached p {
    font-size: 1rem;
  }
}
</style>
